import { useMutation, useQueryClient } from '@tanstack/react-query';

import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import { hasKey } from '~/utils';

type PatchIntakeAttributes = {
  firstDayOfClassOn?: string;
  lastDayOfClassOn?: string;
  acceptingAppsStartsAt?: string;
  acceptingAppsClosesAt?: string;
  refundDeadlineAt?: string;
  deferralDeadlineAt?: string;
  applicationFee?: number;
};

type PatchIntakeResponse = {
  data: unknown;
};

type PatchIntakeRequest = {
  data: {
    id: string;
    type: 'intake';
    attributes: PatchIntakeAttributes;
  };
};

export function usePatchIntake(id: string) {
  const request = useRequest();
  const queryClient = useQueryClient();

  const { isLoading, error, mutateAsync } = useMutation(
    ['patchIntake', id],
    async (attributes: PatchIntakeAttributes) => {
      const body: PatchIntakeRequest = {
        data: {
          id,
          type: 'intake',
          attributes,
        },
      };

      const response = await request<PatchIntakeResponse>(`${config.apiHost}/private/v1/intakes/${id}`, {
        method: 'PATCH',
        body,
        isExpectedResponse,
      });

      await queryClient.invalidateQueries(['programIntake', id]);

      return response;
    }
  );

  return {
    patchIntake: mutateAsync,
    patchIntakeError: error,
    isPatchingIntake: isLoading,
  };
}

function isExpectedResponse(res: unknown): res is PatchIntakeResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
